import { Route } from 'react-router';

import { HealthcareProviderType } from '@dreryk/egabinet-healthcare-provider-frontend-api';

import { assertUnreachable, PromiseVoidFunction } from '@dreryk/react-common';

import { protectFeature } from '~libs.shared.system';
import { useGetCurrentHealthcareProviderType } from '~libs.shared.account';

import { HealthcareProviderPanel } from './healthcareinstitution/HealthcareProviderPanel';

export const HEALTHCARE_PRACTICE_PATH = '/moja-praktyka';
export const HEALTHCARE_ORGANIZATION_PATH = '/moj-podmiot';

type HEALTHCARE_PROVIDER_PATH = typeof HEALTHCARE_PRACTICE_PATH | typeof HEALTHCARE_ORGANIZATION_PATH;

export const getHealthcareProviderPath = (healthcareProviderType: HealthcareProviderType): HEALTHCARE_PROVIDER_PATH => {
   switch (healthcareProviderType) {
      case 'HEALTHCARE_PRACTICE':
         return HEALTHCARE_PRACTICE_PATH;
      case 'HEALTHCARE_ORGANIZATION':
         return HEALTHCARE_ORGANIZATION_PATH;
      default:
         return assertUnreachable(healthcareProviderType);
   }
};

export enum HealthcareProviderNavParams {
   HEALTHCARE_PRACTICE_DATA = 'dane-praktyki',
   HEALTHCARE_ORGANIZATION_DATA = 'dane-podmiotu',
   COMMUNICATION_WITH_P1 = 'p1',
   DOCPLANNER = 'znany-lekarz',
   NFZ_SETTINGS = 'ustawienia-nfz',
   GENERAL_SETTINGS = 'ustawienia-ogolne',
   AI_ASSISTANT = 'asystent-wizyty',
   EMERGENCY_NOTIFICATION = 'sos-dla-medyka',
   DATA_STORAGE = 'przestrzen-na-dane',
   PRACTITIONERS = 'uzytkownicy',
   NOTIFICATIONS = 'wiadomosci-do-pacjentow',
   PATIENT_PORTAL = 'erejestracja',
   INVOICES = 'faktury',
   ONLINE_PAYMENTS = 'platnosci-online',
}

export const resolveHealthcarePracticeDataNavParam = (
   healthcareProviderType: HealthcareProviderType
): HealthcareProviderNavParams.HEALTHCARE_PRACTICE_DATA | HealthcareProviderNavParams.HEALTHCARE_ORGANIZATION_DATA => {
   switch (healthcareProviderType) {
      case 'HEALTHCARE_PRACTICE':
         return HealthcareProviderNavParams.HEALTHCARE_PRACTICE_DATA;
      case 'HEALTHCARE_ORGANIZATION':
         return HealthcareProviderNavParams.HEALTHCARE_ORGANIZATION_DATA;
      default:
         return assertUnreachable(healthcareProviderType);
   }
};

type HealthcareProviderInternalPath<PATH extends HealthcareProviderNavParams> = `${HEALTHCARE_PROVIDER_PATH}/${PATH}`;

export const getHealthcareProviderDataPath = (
   healthcareProviderType: HealthcareProviderType
): HealthcareProviderInternalPath<
   HealthcareProviderNavParams.HEALTHCARE_PRACTICE_DATA | HealthcareProviderNavParams.HEALTHCARE_ORGANIZATION_DATA
> =>
   `${getHealthcareProviderPath(healthcareProviderType)}/${resolveHealthcarePracticeDataNavParam(
      healthcareProviderType
   )}` as const;

export const getHealthcarePracticeP1Path = (
   healthcareProviderType: HealthcareProviderType
): HealthcareProviderInternalPath<HealthcareProviderNavParams.COMMUNICATION_WITH_P1> =>
   `${getHealthcareProviderPath(healthcareProviderType)}/${HealthcareProviderNavParams.COMMUNICATION_WITH_P1}` as const;

const HealthcareOrganizationNfzSettingsPath = `${HEALTHCARE_ORGANIZATION_PATH}/${HealthcareProviderNavParams.NFZ_SETTINGS}` as const;
const HealthcarePracticeNfzSettingsPath = `${HEALTHCARE_PRACTICE_PATH}/${HealthcareProviderNavParams.NFZ_SETTINGS}` as const;
type HealthcareProviderNfzSettingsPath =
   | typeof HealthcareOrganizationNfzSettingsPath
   | typeof HealthcarePracticeNfzSettingsPath;
export const getHealthcareNfzSettingsPath = (
   healthcareProviderType: HealthcareProviderType
): HealthcareProviderNfzSettingsPath => {
   switch (healthcareProviderType) {
      case 'HEALTHCARE_ORGANIZATION':
         return HealthcareOrganizationNfzSettingsPath;
      case 'HEALTHCARE_PRACTICE':
         return HealthcarePracticeNfzSettingsPath;
      default:
         return assertUnreachable(healthcareProviderType);
   }
};

const HealthcareOrganizationDocplannerIntegrationPath = `${HEALTHCARE_ORGANIZATION_PATH}/${HealthcareProviderNavParams.DOCPLANNER}` as const;
const HealthcarePracticeDocplannerIntegrationPath = `${HEALTHCARE_PRACTICE_PATH}/${HealthcareProviderNavParams.DOCPLANNER}` as const;
type HealthcareProviderDocplannerIntegrationPath =
   | typeof HealthcareOrganizationDocplannerIntegrationPath
   | typeof HealthcarePracticeDocplannerIntegrationPath;
export const getDocplannerIntegrationPath = (
   healthcareProviderType: HealthcareProviderType
): HealthcareProviderDocplannerIntegrationPath => {
   switch (healthcareProviderType) {
      case 'HEALTHCARE_ORGANIZATION':
         return HealthcareOrganizationDocplannerIntegrationPath;
      case 'HEALTHCARE_PRACTICE':
         return HealthcarePracticeDocplannerIntegrationPath;
      default:
         return assertUnreachable(healthcareProviderType);
   }
};

type HealthcareProviderModuleRouterOptions = {
   verifyAccountConfiguration: PromiseVoidFunction;
};

export const useHealthcareProviderModuleRouter = (
   options: HealthcareProviderModuleRouterOptions
): JSX.Element | null => {
   const healthcareProviderType = useGetCurrentHealthcareProviderType();

   return protectFeature(
      'updatingProviderData',
      <Route path={getHealthcareProviderPath(healthcareProviderType)}>
         <HealthcareProviderPanel
            healthcareProviderType={healthcareProviderType}
            verifyAccountConfiguration={options.verifyAccountConfiguration}
         />
      </Route>,
      null
   );
};
